import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';

/*
 * Org whitelabel — custom gallery domains + sender addresses. The
 * classic page called every hook with its arguments swapped (URLs like
 * /:settingId/whitelabel/:orgId) and its detail view was unreachable;
 * this page calls the hook as declared and folds view/edit into one
 * dialog.
 */

interface WhitelabelRow {
    id: number;
    name: string;
    domain: string;
    email_from_name: string;
    email_from_address: string;
    email_reply_to_address: string;
}

const EMPTY_DRAFT = {
    name: '',
    domain: '',
    emailFromName: '',
    emailFromAddress: '',
    emailReplyToAddress: '',
};

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function OrgWhitelabelV2() {
    const { orgId } = useUserOrg();
    const {
        getWhitelabelSettingsByOrgId,
        createWhitelabelSettings,
        updateWhitelabelSettings,
        deleteWhitelabelSettings,
    } = useWhitelabelSettings();

    const [rows, setRows] = useState<WhitelabelRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialog, setDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; row: WhitelabelRow } | null
    >(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const dialogRef = useDialogRef(dialog != null, () => setDialog(null));

    const load = async () => {
        const data =
            (await getWhitelabelSettingsByOrgId(orgId).catch(() => [])) ??
            [];
        setRows(data as any);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setDialogError(null);
        setDialog({ kind: 'create' });
    };

    const openEdit = (row: WhitelabelRow) => {
        setDraft({
            name: row.name ?? '',
            domain: row.domain ?? '',
            emailFromName: row.email_from_name ?? '',
            emailFromAddress: row.email_from_address ?? '',
            emailReplyToAddress: row.email_reply_to_address ?? '',
        });
        setDialogError(null);
        setDialog({ kind: 'edit', row });
    };

    const validate = () => {
        if (draft.name.trim().length < 2) return 'Name is required.';
        if (!/^https?:\/\/.+\..+/.test(draft.domain.trim()))
            return 'Domain must be a full URL, e.g. https://photos.example.com';
        if (!draft.emailFromAddress.includes('@'))
            return 'From address must be an email.';
        if (
            draft.emailReplyToAddress &&
            !draft.emailReplyToAddress.includes('@')
        )
            return 'Reply-to must be an email.';
        return null;
    };

    const submit = async () => {
        if (!dialog || busy) return;
        const problem = validate();
        if (problem) {
            setDialogError(problem);
            return;
        }
        setBusy(true);
        setDialogError(null);
        const body = {
            name: draft.name.trim(),
            domain: draft.domain.trim(),
            emailFromName: draft.emailFromName.trim(),
            emailFromAddress: draft.emailFromAddress.trim(),
            emailReplyToAddress:
                draft.emailReplyToAddress.trim() ||
                draft.emailFromAddress.trim(),
        };
        const ok =
            dialog.kind === 'create'
                ? await createWhitelabelSettings(orgId, {
                      ...body,
                      orgId,
                  }).catch(() => null)
                : await updateWhitelabelSettings(
                      orgId,
                      dialog.row.id,
                      body,
                  ).catch(() => null);
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success(
                dialog.kind === 'create' ? 'Whitelabel added' : 'Saved',
            );
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const remove = async (row: WhitelabelRow) => {
        if (
            !window.confirm(
                `Delete the "${row.name}" whitelabel? Galleries on ${row.domain} fall back to the global URL.`,
            )
        )
            return;
        const prev = rows;
        setRows((current) => current.filter((r) => r.id !== row.id));
        const ok = await deleteWhitelabelSettings(orgId, row.id).catch(
            () => false,
        );
        if (!ok) {
            setRows(prev);
            toast.error("Couldn't delete the whitelabel.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Whitelabel"
                desc="Custom gallery domains and sender addresses. Galleries on a whitelabel domain send from its address; everything else falls back to the global gallery URL."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {rows.length} domain
                            {rows.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add whitelabel
                        </button>
                    </>
                }
            >
                {rows.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No whitelabels yet — galleries use the global URL.
                    </p>
                )}
                {rows.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="w-[150px] flex-none truncate font-semibold">
                            {row.name}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                            {(row.domain ?? '').replace(/^https?:\/\//, '')}
                        </span>
                        <span className="hidden min-w-0 flex-1 truncate text-muted-foreground sm:block">
                            {row.email_from_address}
                        </span>
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                edit
                            </button>
                            <button
                                type="button"
                                title="Delete whitelabel"
                                onClick={() => remove(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
            </SettingsCard>

            {dialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setDialog(null)}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            dialog.kind === 'create'
                                ? 'Add whitelabel'
                                : `Edit ${dialog.row.name}`,
                        )}
                        className="w-full max-w-[420px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            {dialog.kind === 'create'
                                ? 'Add whitelabel'
                                : `Edit ${dialog.row.name}`}
                        </div>
                        <label className={FIELD_LABEL}>Name</label>
                        <input
                            autoFocus
                            className="ps-input mb-3 w-full text-[13px]"
                            placeholder="Client brand"
                            value={draft.name}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    name: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>Domain</label>
                        <input
                            className="ps-input mb-3 w-full font-mono text-[12px]"
                            placeholder="https://photos.example.com"
                            value={draft.domain}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    domain: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>From name</label>
                        <input
                            className="ps-input mb-3 w-full text-[13px]"
                            placeholder="Client Events"
                            value={draft.emailFromName}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    emailFromName: e.target.value,
                                }))
                            }
                        />
                        <div className="mb-4 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>
                                    From address
                                </label>
                                <input
                                    type="email"
                                    className="ps-input w-full text-[13px]"
                                    placeholder="photos@example.com"
                                    value={draft.emailFromAddress}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            emailFromAddress:
                                                e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>
                                    Reply-to{' '}
                                    <span className="font-normal text-faint">
                                        — optional
                                    </span>
                                </label>
                                <input
                                    type="email"
                                    className="ps-input w-full text-[13px]"
                                    value={draft.emailReplyToAddress}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            emailReplyToAddress:
                                                e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submit}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy
                                    ? 'Saving…'
                                    : dialog.kind === 'create'
                                    ? 'Add whitelabel'
                                    : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDialog(null)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
